import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { FileText, ArrowLeft, TrendingUp, Users2, Star, BarChart2 } from "lucide-react";
import { Link } from "react-router-dom";
import useAssessmentData from "../hooks/useAssessmentData";

/**
 * Reports — the author's REAL analytics, not generated PDFs.
 *
 * This page showed four invented report documents ("Q1 Performance Report",
 * PDF · 2.4 MB, 85% pass rate, 4.5/5 rating) that no API produced and that the
 * backend cannot generate or download. There is no reports endpoint and no
 * document store behind "Download", so both were removed rather than left as
 * buttons that do nothing.
 *
 * What replaces them is genuine reporting data:
 *   GET /api/resource-person/courses/:id/analytics  → per-course figures
 *   GET /api/resource-person/earnings/summary      → wallet balances
 *   GET /api/resource-person/earnings/analytics     → weekly/monthly trends
 *
 * Any figure the API does not report renders as "—" instead of an invented value.
 */
const metric = (value, suffix = "") =>
  value === null || value === undefined || Number.isNaN(Number(value)) ? "—" : `${value}${suffix}`;

export default function ReportsPage() {
  const {
    courses,
    coursesLoading,
    coursesError,
    selectedCourseId,
    setSelectedCourseId,
    courseAnalytics,
    courseAnalyticsLoading,
    earningsSummary,
    earningsAnalytics,
  } = useAssessmentData();

  // Course analytics is a free-form payload; pull the first sensible value for
  // each figure rather than assuming one exact field name.
  // Course analytics and earnings analytics are both free-form payloads; pull
  // the first sensible value for each figure rather than assuming one exact
  // field name. `pickFrom` reads a specific source object.
  const pickFrom = (source, ...names) => {
    const a = source || {};
    for (const name of names) {
      if (a[name] !== undefined && a[name] !== null && a[name] !== "") return a[name];
    }
    return null;
  };
  const pick = (...names) => pickFrom(courseAnalytics, ...names);
  const asNumber = (...names) => {
    const raw = pick(...names);
    // Number(null) is 0, so a missing figure must be rejected BEFORE coercing —
    // otherwise "no data" renders as a confident "0".
    if (raw === null || raw === undefined || raw === "") return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  };

  const students = asNumber("students", "totalStudents", "enrolledStudents", "studentsEnrolled");
  const completions = asNumber("completions", "completed", "completedEnrollments");
  const avgScore = asNumber("averageScore", "avgScore", "average");
  const passRate = asNumber("passRate", "completionRate");
  const totalRevenue = pick("revenue", "totalRevenue", "earnings", "total");

  // Earnings trend comes from /earnings/analytics, falling back to the course
  // payload when the analytics endpoint returned nothing.
  const earningsSource = earningsAnalytics || courseAnalytics || null;
  const weekly = pickFrom(earningsSource, "weekly", "thisWeek", "week", "weeklyEarnings");
  const monthly = pickFrom(earningsSource, "monthly", "thisMonth", "month", "monthlyEarnings");
  const lifetime = pickFrom(
    earningsSource,
    "total", "lifetime", "lifetimeEarnings", "totalEarnings"
  );

  const detailRows = [
    { label: "Enrolled students", value: metric(students), icon: Users2 },
    { label: "Completions", value: metric(completions), icon: Star },
    { label: "Average score", value: metric(avgScore, avgScore === null ? "" : "%"), icon: BarChart2 },
    { label: "Pass rate", value: metric(passRate, passRate === null ? "" : "%"), icon: TrendingUp },
    { label: "Revenue", value: metric(totalRevenue), icon: FileText },
  ];

  const earningsRows = [
    { label: "This week", value: weekly },
    { label: "This month", value: monthly },
    { label: "Lifetime", value: lifetime },
  ].filter((r) => r.value !== null && r.value !== undefined);

  const empty = !courseAnalyticsLoading && !courseAnalytics && !earningsSummary;

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <div className="p-4 gradient-success rounded-2xl shadow-lg">
            <FileText className="h-8 w-8 text-white" />
          </div>
          <div>
            <h1 className="text-4xl font-extrabold tracking-tight mb-1">Reports</h1>
            <p className="text-muted-foreground text-lg">View and download detailed performance reports</p>
          </div>
        </div>
        <div className="flex gap-3">
          <Link to="/resource-person/assessment">
            <Button variant="outline" size="lg" className="shadow-sm font-medium">
              <ArrowLeft className="mr-2 h-4 w-4" /> Back
            </Button>
          </Link>
          {/* The API has no report-generation endpoint, so this button was removed
              rather than left doing nothing. The page shows live analytics instead. */}
        </div>
      </div>

      {/* Course picker — analytics are per course. */}
      <div className="flex flex-col gap-2 rounded-xl border bg-muted/30 p-4 sm:flex-row sm:items-center">
        <label className="text-sm font-semibold" htmlFor="report-course">Course</label>
        <select
          id="report-course"
          className="flex-1 rounded-lg border bg-background px-3 py-2 text-sm"
          value={selectedCourseId}
          disabled={coursesLoading || !courses.length}
          onChange={(e) => setSelectedCourseId(e.target.value)}
        >
          {courses.length ? (
            courses.map((c) => (
              <option key={c.id} value={c.id}>{c.title || "Untitled course"}</option>
            ))
          ) : (
            <option value="">{coursesLoading ? "Loading courses…" : "No courses yet"}</option>
          )}
        </select>
      </div>

      {coursesError ? (
        <p className="m-0 rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-700">{coursesError}</p>
      ) : null}

      {/* Real figures from the API — "—" where it reported nothing. */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {detailRows.map((stat, i) => (
          <Card key={stat.label} className="border-0 shadow-md">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-muted-foreground">{stat.label}</p>
                <stat.icon className="h-5 w-5 text-success" />
              </div>
              <p className="text-3xl font-extrabold">{stat.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Earnings trend from /earnings/analytics */}
      {earningsRows.length ? (
        <Card className="border-0 shadow-md">
          <CardContent className="p-6">
            <h2 className="mb-4 flex items-center gap-2 text-lg font-bold">
              <TrendingUp className="h-5 w-5 text-success" /> Earnings trend
            </h2>
            <div className="grid gap-4 sm:grid-cols-3">
              {earningsRows.map((row) => (
                <div key={row.label} className="rounded-xl bg-muted/40 p-4">
                  <p className="text-xs text-muted-foreground">{row.label}</p>
                  <p className="mt-1 text-xl font-bold">{metric(row.value)}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      ) : null}

      {empty ? (
        <Card className="border-0 shadow-md">
          <CardContent className="p-10 text-center">
            <FileText className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
            <p className="mb-1 font-semibold">No analytics reported yet</p>
            <p className="m-0 text-sm text-muted-foreground">
              Figures appear once learners enroll in and progress through a course.
            </p>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}